import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useStore } from '../../context/StoreContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Buildings, Clock, ShareNetwork, Percent, ChatCircle, FloppyDisk } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

const CompanyPage = () => {
  const { currentStore } = useStore();
  const [data, setData] = useState({
    company_name: '',
    company_siret: '',
    contact_email: '',
    contact_phone: '',
    address_line: '',
    postal_code: '',
    city: '',
    country: 'France',
    social: { facebook: '', instagram: '', tiktok: '', linkedin: '' },
    opening_hours: DAYS.reduce((acc, d) => ({ ...acc, [d]: { open: '', close: '', open2: '', close2: '', closed: false } }), {}),
    tax_rate_default: 20,
    tax_rate_by_category: [],
    review_link: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [identity, setIdentity] = useState({});

  const fetchData = useCallback(async () => {
    if (!currentStore) return;
    try {
      const { data: store } = await axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true });
      const c = store?.company || {};
      setIdentity(store?.identity || {});
      setData(prev => ({
        ...prev,
        ...c,
        social: { ...prev.social, ...(c.social || {}) },
        opening_hours: { ...prev.opening_hours, ...(c.opening_hours || {}) },
      }));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentStore]);

  const prefillFromIdentity = () => {
    setData(prev => ({
      ...prev,
      company_name: prev.company_name || identity.company_name || '',
      contact_email: prev.contact_email || identity.contact_email || '',
      contact_phone: prev.contact_phone || identity.contact_phone || '',
      address_line: prev.address_line || identity.contact_address || '',
      social: {
        ...prev.social,
        facebook: prev.social.facebook || identity.social_facebook || '',
        instagram: prev.social.instagram || identity.social_instagram || '',
        tiktok: prev.social.tiktok || identity.social_tiktok || '',
      },
    }));
    toast.info('Champs vides préremplis depuis l\'Identité du site — vérifiez puis Enregistrez');
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${currentStore.id}`, { company: data }, { withCredentials: true });
      toast.success('Informations entreprise enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const upd = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const updSocial = (k, v) => setData(prev => ({ ...prev, social: { ...prev.social, [k]: v } }));
  const updHours = (day, patch) => setData(prev => ({ ...prev, opening_hours: { ...prev.opening_hours, [day]: { ...prev.opening_hours[day], ...patch } } }));

  if (loading) return <div className="p-12 text-center text-slate-400">Chargement…</div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="company-page">
      <div>
        <p className="text-[10px] md:text-xs uppercase tracking-widest text-slate-400 font-semibold">Entreprise</p>
        <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Informations de l'entreprise</h1>
      </div>

      <Card className="border-blue-200 bg-blue-50/60">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="text-sm text-blue-900 font-semibold">📍 Source des coordonnées affichées sur le site</p>
            <p className="text-xs text-blue-800 mt-1">Les informations ci-dessous alimentent : la <strong>page Contact</strong>, le <strong>pied de page</strong> du site et les <strong>signatures des emails</strong>. Si un champ est vide, la valeur de secours vient d'Identité du site.</p>
          </div>
          <Button variant="outline" size="sm" onClick={prefillFromIdentity} className="flex-shrink-0" data-testid="prefill-identity-btn">
            Préremplir depuis l'Identité
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Buildings size={18} />Coordonnées</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Raison sociale</Label><Input value={data.company_name} onChange={e => upd('company_name', e.target.value)} data-testid="company-name" /></div>
          <div><Label className="text-xs">SIRET</Label><Input value={data.company_siret} onChange={e => upd('company_siret', e.target.value)} data-testid="company-siret" /></div>
          <div><Label className="text-xs">Email de contact</Label><Input type="email" value={data.contact_email} onChange={e => upd('contact_email', e.target.value)} data-testid="company-email" /></div>
          <div><Label className="text-xs">Téléphone</Label><Input value={data.contact_phone} onChange={e => upd('contact_phone', e.target.value)} data-testid="company-phone" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Adresse</Label><Input value={data.address_line} onChange={e => upd('address_line', e.target.value)} /></div>
          <div><Label className="text-xs">Code postal</Label><Input value={data.postal_code} onChange={e => upd('postal_code', e.target.value)} /></div>
          <div><Label className="text-xs">Ville</Label><Input value={data.city} onChange={e => upd('city', e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Clock size={18} />Horaires d'ouverture</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Affichés automatiquement sur la page Contact du site. Renseignez la 2ᵉ plage pour une coupure méridienne (ex : 8h-12h30 puis 15h30-19h).</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {DAYS.map(day => (
            <div key={day} className="flex flex-wrap items-center gap-2" data-testid={`hours-${day}`}>
              <div className="w-24 capitalize text-sm font-medium">{day}</div>
              <label className="flex items-center gap-1 text-xs text-slate-500">
                <input type="checkbox" checked={!!data.opening_hours[day]?.closed} onChange={e => updHours(day, { closed: e.target.checked })} />
                Fermé
              </label>
              {!data.opening_hours[day]?.closed && (
                <>
                  <Input type="time" className="w-26 h-8" value={data.opening_hours[day]?.open || ''} onChange={e => updHours(day, { open: e.target.value })} />
                  <span className="text-slate-400">→</span>
                  <Input type="time" className="w-26 h-8" value={data.opening_hours[day]?.close || ''} onChange={e => updHours(day, { close: e.target.value })} />
                  <span className="text-slate-300 text-xs px-1">puis</span>
                  <Input type="time" className="w-26 h-8" value={data.opening_hours[day]?.open2 || ''} onChange={e => updHours(day, { open2: e.target.value })} />
                  <span className="text-slate-400">→</span>
                  <Input type="time" className="w-26 h-8" value={data.opening_hours[day]?.close2 || ''} onChange={e => updHours(day, { close2: e.target.value })} />
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShareNetwork size={18} />Réseaux sociaux</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {['facebook', 'instagram', 'tiktok', 'linkedin'].map(net => (
            <div key={net}><Label className="text-xs capitalize">{net}</Label><Input placeholder={`https://${net}.com/...`} value={data.social[net] || ''} onChange={e => updSocial(net, e.target.value)} data-testid={`social-${net}`} /></div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Percent size={18} />TVA</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Taux TVA par défaut de la boutique</Label>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.5" min="0" max="30" value={data.tax_rate_default} onChange={e => upd('tax_rate_default', parseFloat(e.target.value) || 0)} className="w-24" data-testid="tax-default" />
              <span className="text-sm text-slate-500">%</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Applicable à toutes les catégories qui n'ont pas de taux personnalisé.</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg text-[12px] text-blue-800">
            💡 Pour un taux différent par catégorie (5.5%, 10%, 20%), rendez-vous dans <b>Catalogue → Catégories</b> et renseignez le champ "TVA (%)".
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ChatCircle size={18} />Collecte d'avis clients</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Lien externe pour laisser un avis (Google Business, Trustpilot, ...)</Label>
            <Input placeholder="https://g.page/r/..." value={data.review_link} onChange={e => upd('review_link', e.target.value)} data-testid="review-link" />
          </div>
          <p className="text-[12px] text-slate-500">Ce lien apparaîtra dans l'email de demande d'avis envoyé au client après une commande livrée (bouton "Demander un avis" sur la fiche commande).</p>
        </CardContent>
      </Card>

      <div className="sticky bottom-3 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0052FF] shadow-lg gap-2" data-testid="company-save">
          <FloppyDisk size={16} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

export default CompanyPage;
